import React, { useMemo, useState } from 'react'
import { Card, spacing } from '@retailer-platform/shared-components'
import { ConfirmIcon, EditIcon, RefundIcon, TrashIcon } from '@instacart/ids-core'
import { IconButton } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import { type FieldDefinition, type Mutation } from '../RegexForm'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { RegexCardBody } from './RegexCardBody'

interface Props {
  onDelete: () => void
  onChange: (mutation: Mutation) => void
  mutation: Mutation
  isNew: boolean
  bannedCombinations: FieldDefinition[]
  readOnly: boolean
}

interface State {
  isEditing: boolean
  mutationBuffer: Mutation
}

const ActionRow = styled.div({
  display: 'flex',
  flexDirection: 'row',
  gap: spacing.X8,
})

export const RegexCard = ({
  onDelete,
  onChange,
  mutation,
  isNew,
  bannedCombinations,
  readOnly,
}: Props) => {
  const [state, setState] = useState<State>({ isEditing: isNew, mutationBuffer: mutation })

  const i18n = useDomainMessages({
    edit: 'catalogAdminDomain.attributeManagement.regex.form.mutation.editbutton.accessibleLabel',
    delete:
      'catalogAdminDomain.attributeManagement.regex.form.mutation.deletebutton.accessibleLabel',
    cancel:
      'catalogAdminDomain.attributeManagement.regex.form.mutation.cancelbutton.accessibleLabel',
    save: 'catalogAdminDomain.attributeManagement.regex.form.mutation.savebutton.accessibleLabel',
    newMutationTitle: 'catalogAdminDomain.attributeManagement.regex.form.mutation.newMutationTitle',
  })

  const fieldsDefinitionValid = useMemo(() => {
    if (
      bannedCombinations.some(
        b =>
          b.inputField === state.mutationBuffer.inputField &&
          b.outputField === state.mutationBuffer.outputField
      )
    ) {
      return false
    }
    return state.mutationBuffer.inputField.length > 0 && state.mutationBuffer.outputField.length > 0
  }, [bannedCombinations, state.mutationBuffer])

  const anythingChanged = useMemo(
    () => JSON.stringify(state.mutationBuffer) !== JSON.stringify(mutation),
    [state.mutationBuffer, mutation]
  )

  const viewActions = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={EditIcon}
          onPress={() => setState({ ...state, isEditing: true })}
          disabled={readOnly}
          accessibleLabel={i18n.edit}
        />
        <IconButton
          icon={TrashIcon}
          onPress={onDelete}
          disabled={readOnly}
          accessibleLabel={i18n.delete}
        />
      </ActionRow>
    ),
    [onDelete, i18n, state, readOnly]
  )

  const editActions = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={RefundIcon}
          onPress={() => setState({ ...state, isEditing: false, mutationBuffer: mutation })}
          disabled={readOnly}
          accessibleLabel={i18n.cancel}
        />
        <IconButton
          icon={ConfirmIcon}
          disabled={!anythingChanged || !fieldsDefinitionValid || readOnly}
          onPress={() => {
            onChange(state.mutationBuffer)
            setState({ ...state, isEditing: false })
          }}
          accessibleLabel={i18n.save}
        />
      </ActionRow>
    ),
    [i18n, state, readOnly, anythingChanged, fieldsDefinitionValid, mutation, onChange]
  )
  const newActions = useMemo(
    () => (
      <ActionRow>
        <IconButton
          icon={TrashIcon}
          onPress={onDelete}
          disabled={readOnly}
          accessibleLabel={i18n.cancel}
        />
        <IconButton
          icon={ConfirmIcon}
          disabled={!fieldsDefinitionValid || readOnly}
          onPress={() => onChange(state.mutationBuffer)}
          accessibleLabel={i18n.save}
        />
      </ActionRow>
    ),
    [onChange, onDelete, state.mutationBuffer, i18n, readOnly, fieldsDefinitionValid]
  )

  const actions = useMemo(() => {
    if (isNew) {
      return newActions
    }
    return state.isEditing ? editActions : viewActions
  }, [isNew, state.isEditing, editActions, viewActions, newActions])

  return (
    <Card
      title={isNew ? i18n.newMutationTitle : `${mutation.inputField} → ${mutation.outputField}`}
      headerContent={actions}
      style={{ width: '100%' }}
      showDropShadow
    >
      <RegexCardBody
        mutation={state.mutationBuffer}
        editting={state.isEditing}
        onChange={(m: Mutation) => {
          setState({ ...state, mutationBuffer: m })
        }}
        isValid={fieldsDefinitionValid}
        readOnly={readOnly || !state.isEditing}
      />
    </Card>
  )
}
